/**
 * AiSuggestionPanel -- Displays an AI suggestion with Accept/Dismiss controls.
 *
 * Renders below the triggering field. Visually distinct from regular form
 * fields via a soft blue-tinted background. Handles loading (skeleton),
 * error (amber inline message), and suggestion (content + actions) states.
 *
 * Hidden entirely when there is nothing to display (no suggestion, no error,
 * not loading).
 *
 * ADR-022: Domain-specific compound display component.
 * @see ADR-055 for AI-assisted resume feature architecture.
 */
import type { ReactNode } from 'react';
import { Button } from '../ui/Button';

interface AiSuggestionPanelProps {
  /** The AI suggestion data, or null when idle. */
  suggestion: string | Record<string, unknown> | null;
  /** Error message from the AI service, or null. */
  error: string | null;
  /** Accept the suggestion. */
  onAccept: () => void;
  /** Dismiss the suggestion. */
  onDismiss: () => void;
  /** Show a loading skeleton while the AI is thinking. */
  isLoading?: boolean;
  /** Custom render function for structured (non-string) suggestions. */
  renderSuggestion?: (suggestion: unknown) => ReactNode;
}

/**
 * Default renderer for suggestion content. Handles strings directly
 * and falls back to a key-value list for object suggestions.
 */
function defaultRenderSuggestion(suggestion: unknown): ReactNode {
  if (typeof suggestion === 'string') {
    return <p className="ai-suggestion-panel__text">{suggestion}</p>;
  }

  if (suggestion && typeof suggestion === 'object') {
    const entries = Object.entries(suggestion as Record<string, unknown>);
    return (
      <dl className="ai-suggestion-panel__data">
        {entries.map(([key, value]) => {
          if (value === null || value === undefined) return null;
          const displayKey = key.replace(/_/g, ' ');
          return (
            <div key={key} className="ai-suggestion-panel__data-row">
              <dt className="ai-suggestion-panel__data-key">{displayKey}</dt>
              <dd className="ai-suggestion-panel__data-value">
                {Array.isArray(value)
                  ? (value as unknown[]).map((item) => String(item)).join(', ')
                  : String(value)}
              </dd>
            </div>
          );
        })}
      </dl>
    );
  }

  return null;
}

export function AiSuggestionPanel({
  suggestion,
  error,
  onAccept,
  onDismiss,
  isLoading = false,
  renderSuggestion,
}: AiSuggestionPanelProps) {
  // Hidden when there is nothing to show.
  if (!suggestion && !error && !isLoading) return null;

  const render = renderSuggestion ?? defaultRenderSuggestion;

  return (
    <div
      className={`ai-suggestion-panel ${error ? 'ai-suggestion-panel--error' : ''}`}
      role="region"
      aria-label="AI suggestion"
      aria-live="polite"
    >
      {/* Loading state */}
      {isLoading && (
        <div className="ai-suggestion-panel__loading">
          <span className="ai-suggestion-panel__pulse" aria-hidden="true" />
          <span className="ai-suggestion-panel__loading-text">Thinking...</span>
        </div>
      )}

      {/* Error state */}
      {error && !isLoading && (
        <div className="ai-suggestion-panel__error-content">
          <p className="ai-suggestion-panel__error-text">
            {error}
          </p>
          <Button variant="secondary" size="sm" onClick={onDismiss}>
            Dismiss
          </Button>
        </div>
      )}

      {/* Suggestion state */}
      {suggestion && !isLoading && !error && (
        <>
          <div className="ai-suggestion-panel__body">
            {render(suggestion)}
          </div>
          <div className="ai-suggestion-panel__actions">
            <Button variant="secondary" size="sm" onClick={onDismiss}>
              Dismiss
            </Button>
            <Button variant="primary" size="sm" onClick={onAccept}>
              Accept
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
